/* eslint-disable @typescript-eslint/only-throw-error */
import { AnnaError } from "@anna-money/anna-web-lib/utils";
import { makeAutoObservable } from "mobx";

import { type IssuesClient } from "./issuesClient";
import { type IssueData } from "./issuesTypes";

export enum IssueKey {
  Init = "init",
  Company = "company",
  Director = "director",
  CompanyHouse = "companyhouse",
}

enum IssueSkipConfirmationScreenType {
  CompanyCheck = "company.check",
  CompanyHouseSingleDirectorCheck = "companyhouse.ch_code",
  CompanyHouseMultiDirectorCheck = "companyhouse.all_ch_codes",
}

export type IssueFieldQuestionMap<T> = Record<string, T>;

export type ClientIssueData = {
  id: string;
  key: IssueKey;
  field: string;
};

export interface IIssuesStore {
  issues: IssueData[];
  resolveIssue: (issueId: string) => Promise<void>;
  hasIssues: () => boolean;
  getFirstIssueForKey: (key: IssueKey) => ClientIssueData | undefined;
  getFirstResolvedIssueKey: () => IssueKey | undefined;
  getIssuesForKey: (key: IssueKey) => ClientIssueData[];
  hasIssueForKey: (key: IssueKey) => boolean;
  skipConfirmScreen: () => boolean;
}

export class IssuesStore implements IIssuesStore {
  private readonly _companyIssuesClient: IssuesClient;

  private _issues: IssueData[] = [];
  private _parsedIssues: ClientIssueData[] = [];
  private _resolvedIssues: IssueData[] = [];

  constructor(companyIssuesClient: IssuesClient) {
    this._companyIssuesClient = companyIssuesClient;

    makeAutoObservable(this);
  }

  get issues(): IssueData[] {
    return this._issues;
  }

  get resolvedIssues(): IssueData[] {
    return this._resolvedIssues;
  }

  async init(): Promise<void> {
    await this._refreshIssues();
  }

  async resolveIssue(issueId: string): Promise<void> {
    await this._companyIssuesClient.resolveIssue(issueId);
    await this._refreshIssues();
  }

  hasIssues(): boolean {
    return Boolean(this._issues.length);
  }

  hasIssuesWithPath(): boolean {
    return this._issues.some((issue) => !!issue.path);
  }

  hasIssueForKey(key: IssueKey): boolean {
    for (const issue of this._parsedIssues) {
      if (issue.key === key) {
        return true;
      }
    }

    return false;
  }

  getFirstIssueForKey(key: IssueKey): ClientIssueData | undefined {
    return this._parsedIssues.find((issue) => issue.key === key);
  }

  getIssuesForKey(key: IssueKey): ClientIssueData[] {
    return this._parsedIssues.filter((issue) => issue.key === key);
  }

  getFirstResolvedIssueKey(): IssueKey | undefined {
    const resolvedIssue = this._resolvedIssues[0];

    if (!resolvedIssue) {
      return undefined;
    }

    const splittedPath = resolvedIssue.path.split(".");
    const step = splittedPath[1]?.toLowerCase();

    if (!Object.values(IssueKey).includes(splittedPath[0] as IssueKey)) {
      throw new AnnaError("Unexpected step name from the issue");
    }

    return step as IssueKey;
  }

  skipConfirmScreen(): boolean {
    // skip the confirm step and show success screen if the issue path matches any IssueSuccessType
    const issueSuccessTypes = Object.values(IssueSkipConfirmationScreenType);

    for (const issue of this._resolvedIssues) {
      if (issueSuccessTypes.includes(issue.path as IssueSkipConfirmationScreenType)) {
        return true;
      }
    }

    return false;
  }

  private async _refreshIssues(): Promise<void> {
    const issues = await this._companyIssuesClient.getIssues();

    this._resolvedIssues = issues.filter((issue) => issue.resolved);
    this._issues = issues.filter((issue) => !issue.resolved);

    this._parsedIssues = this._issues.map((issue) => {
      const splittedPath = issue.path.split(".");
      const step = splittedPath[0]?.toLowerCase();

      if (!Object.values(IssueKey).includes(step as IssueKey)) {
        throw new AnnaError("Unexpected step name from the issue");
      }
      const field = splittedPath[1]?.toLowerCase() ?? "";

      return {
        id: issue.id,
        key: step as IssueKey,
        field,
      };
    });
  }
}
